import { useState } from "react";
import type { StudioEffect, StudioEffectType } from "@loumy/contracts";
import { STUDIO_EFFECT_TYPES } from "@loumy/contracts";
import { Button, ProgressBar, Segmented, Select, Slider, Toggle, cx } from "../ui";
import { EFFECT_LABELS } from "./effectStack";
import type { StudioResultKind } from "./studioPlan";

// §9.17 — Op-Stack als komponierbarer EFFEKT-Editor (rechts). „+ Effekt" fügt Layer hinzu, jeder
// Layer ist ein-/ausschaltbar, umordnenbar (↑↓ + Drag) und löschbar; die Reihenfolge = Wirkung
// (Worker führt sie von oben nach unten aus). Darunter die Output-Art + Save-Leiste mit Σ-Preis.
// Reine Pixel-Effekte sind kostenlos; nur ein aktiver „Hintergrund entfernen"-Layer kostet (Steps).

function clampByte(n: number): number {
  return Math.max(0, Math.min(255, Math.round(n)));
}
function rgbToHex(rgb: readonly number[]): string {
  return "#" + [rgb[0] ?? 0, rgb[1] ?? 0, rgb[2] ?? 0].map((c) => clampByte(c).toString(16).padStart(2, "0")).join("");
}
function hexToRgb(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return [0, 0, 0];
  const n = parseInt(m[1]!, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

type PatchFn = (patch: Record<string, unknown>) => void;

function Row({ label, value, children }: { label: string; value?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted">{label}</span>
        {value !== undefined && <span className="text-xs tabular-nums text-text">{value}</span>}
      </div>
      {children}
    </div>
  );
}

function ColorRow({ label, value, onChange }: { label: string; value: readonly number[]; onChange: (c: [number, number, number]) => void }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-muted">{label}</span>
      <input
        type="color"
        aria-label={label}
        value={rgbToHex(value)}
        onChange={(e) => onChange(hexToRgb(e.target.value))}
        className="h-7 w-12 cursor-pointer rounded border border-border bg-transparent"
      />
    </div>
  );
}

/** Parameter-Steuerung je Effekt-Typ. */
function EffectParams({ effect, onPatch }: { effect: StudioEffect; onPatch: PatchFn }) {
  switch (effect.type) {
    case "speed":
      return (
        <Row label="Tempo" value={`${effect.factor}×`}>
          <Slider min={0.1} max={10} step={0.1} value={effect.factor} onChange={(v) => onPatch({ factor: v })} />
        </Row>
      );
    case "color":
      return (
        <div className="space-y-2.5">
          <Row label="Helligkeit" value={`${effect.brightness}`}>
            <Slider min={-100} max={100} value={effect.brightness} onChange={(v) => onPatch({ brightness: v })} />
          </Row>
          <Row label="Kontrast" value={`${effect.contrast}`}>
            <Slider min={-100} max={100} value={effect.contrast} onChange={(v) => onPatch({ contrast: v })} />
          </Row>
          <Row label="Sättigung" value={`${effect.saturation}`}>
            <Slider min={-100} max={100} value={effect.saturation} onChange={(v) => onPatch({ saturation: v })} />
          </Row>
          <Row label="Farbton" value={`${effect.hue}°`}>
            <Slider min={-180} max={180} value={effect.hue} onChange={(v) => onPatch({ hue: v })} />
          </Row>
          <Toggle
            checked={effect.tint !== null}
            onChange={(on) => onPatch({ tint: on ? [255, 0, 0] : null, tintStrength: on ? Math.max(50, effect.tintStrength) : 0 })}
            label="Einfärben (Tint)"
          />
          {effect.tint !== null && (
            <>
              <ColorRow label="Tint-Farbe" value={effect.tint} onChange={(c) => onPatch({ tint: c })} />
              <Row label="Stärke" value={`${effect.tintStrength}`}>
                <Slider min={0} max={100} value={effect.tintStrength} onChange={(v) => onPatch({ tintStrength: v })} />
              </Row>
            </>
          )}
        </div>
      );
    case "transform":
      return (
        <div className="space-y-2.5">
          <div className="flex gap-4">
            <Toggle checked={effect.flipH} onChange={(v) => onPatch({ flipH: v })} label="↔ Spiegeln" />
            <Toggle checked={effect.flipV} onChange={(v) => onPatch({ flipV: v })} label="↕ Spiegeln" />
          </div>
          <Row label="Drehen">
            <Segmented<string>
              value={String(effect.rotateQuarters)}
              onChange={(v) => onPatch({ rotateQuarters: Number(v) })}
              options={[
                { value: "0", label: "0°" },
                { value: "1", label: "90°" },
                { value: "2", label: "180°" },
                { value: "3", label: "270°" },
              ]}
            />
          </Row>
          <Row label="Skalieren" value={`${effect.scale}%`}>
            <Slider min={10} max={400} step={5} value={effect.scale} onChange={(v) => onPatch({ scale: v })} />
          </Row>
        </div>
      );
    case "chroma-key":
      return (
        <div className="space-y-2.5">
          <Segmented<"auto" | "green" | "blue">
            value={effect.keyColor}
            onChange={(c) => onPatch({ keyColor: c })}
            options={[
              { value: "auto", label: "Auto" },
              { value: "green", label: "Grün" },
              { value: "blue", label: "Blau" },
            ]}
          />
          <Row label="Toleranz" value={`${effect.tolerance}`}>
            <Slider min={0} max={100} value={effect.tolerance} onChange={(v) => onPatch({ tolerance: v })} />
          </Row>
        </div>
      );
    case "outline":
      return (
        <div className="space-y-2.5">
          <Row label="Dicke" value={`${effect.thickness} px`}>
            <Slider min={1} max={16} value={effect.thickness} onChange={(v) => onPatch({ thickness: v })} />
          </Row>
          <ColorRow label="Farbe" value={effect.color} onChange={(c) => onPatch({ color: c })} />
        </div>
      );
    case "glow":
      return (
        <div className="space-y-2.5">
          <Row label="Radius" value={`${effect.radius} px`}>
            <Slider min={1} max={64} value={effect.radius} onChange={(v) => onPatch({ radius: v })} />
          </Row>
          <ColorRow label="Farbe" value={effect.color} onChange={(c) => onPatch({ color: c })} />
          <Row label="Stärke" value={`${effect.strength}`}>
            <Slider min={0} max={100} value={effect.strength} onChange={(v) => onPatch({ strength: v })} />
          </Row>
        </div>
      );
    case "pixelate":
      return (
        <div className="space-y-2.5">
          <Row label="Pixelgröße" value={`${effect.cell} px`}>
            <Slider min={1} max={64} value={effect.cell} onChange={(v) => onPatch({ cell: v })} />
          </Row>
          <Row label="Farben (0 = aus)" value={effect.colors === 0 ? "aus" : `${effect.colors}`}>
            <Slider min={0} max={64} value={effect.colors} onChange={(v) => onPatch({ colors: v })} />
          </Row>
          <Toggle checked={effect.upscale} onChange={(v) => onPatch({ upscale: v })} label="Auf Originalgröße zurück" />
        </div>
      );
    case "drop-shadow":
      return (
        <div className="space-y-2.5">
          <Row label="Versatz X" value={`${effect.dx}`}>
            <Slider min={-32} max={32} value={effect.dx} onChange={(v) => onPatch({ dx: v })} />
          </Row>
          <Row label="Versatz Y" value={`${effect.dy}`}>
            <Slider min={-32} max={32} value={effect.dy} onChange={(v) => onPatch({ dy: v })} />
          </Row>
          <Row label="Weichheit" value={`${effect.blur}`}>
            <Slider min={0} max={32} value={effect.blur} onChange={(v) => onPatch({ blur: v })} />
          </Row>
          <ColorRow label="Farbe" value={effect.color} onChange={(c) => onPatch({ color: c })} />
          <Row label="Deckkraft" value={`${effect.opacity}`}>
            <Slider min={0} max={100} value={effect.opacity} onChange={(v) => onPatch({ opacity: v })} />
          </Row>
        </div>
      );
  }
}

export interface OpStackProps {
  /** Effekt-Stack des SELEKTIERTEN Clips (§9.17 — Effekte gelten pro Clip). */
  effects: StudioEffect[];
  /** Label des selektierten Clips („Clip 2 · 0,8 s") bzw. null, wenn keiner gewählt ist. */
  selectedClipLabel: string | null;
  /** Ist überhaupt ein Clip selektiert? (sonst Hinweis statt Effekt-Editor). */
  hasSelection: boolean;
  onAddEffect: (type: StudioEffectType) => void;
  onRemoveEffect: (id: string) => void;
  onToggleEffect: (id: string, enabled: boolean) => void;
  onUpdateEffect: (id: string, patch: Record<string, unknown>) => void;
  onReorderEffect: (from: number, to: number) => void;

  resultKind: StudioResultKind;
  onResultKind: (k: StudioResultKind) => void;
  loop: boolean;
  onLoop: (v: boolean) => void;
  seam: boolean;
  onSeam: (v: boolean) => void;

  canExportWebm: boolean; // Quelle ist Video UND keine WebM-sperrenden Effekte
  webmBlockedByEffects: boolean; // Quelle wäre Video, aber Effekte sperren v1
  canDownload: boolean;
  credits: number | null;
  busy: boolean;
  /** D169: Phasen-Fortschritt der laufenden Save/Download/Export-Operation (null = im Leerlauf). */
  progress: { label: string; value?: number; max?: number } | null;
  onSave: () => void;
  onDownload: () => void;
  onExportWebm: () => void;
}

function EffectCard({
  effect,
  index,
  count,
  onToggle,
  onRemove,
  onPatch,
  onMove,
  onDragStart,
  onDropOn,
}: {
  effect: StudioEffect;
  index: number;
  count: number;
  onToggle: (enabled: boolean) => void;
  onRemove: () => void;
  onPatch: PatchFn;
  onMove: (to: number) => void;
  onDragStart: () => void;
  onDropOn: () => void;
}) {
  return (
    <div
      // NUR der Griff ⠿ ist draggable (s.u.) — sonst fängt ein draggable Karten-Wrapper
      // den Mousedown auf Slider/Color-Inputs ab und der Regler lässt sich nicht ziehen.
      // Die Karte selbst bleibt reines Drop-Target.
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        onDropOn();
      }}
      className={cx("rounded-lg border border-border bg-surface-2", !effect.enabled && "opacity-60")}
    >
      <div className="flex items-center gap-1.5 px-2 py-1.5">
        <span
          draggable
          onDragStart={(e) => {
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("text/plain", String(index)); // Firefox startet Drag nur mit Daten
            onDragStart();
          }}
          className="cursor-grab select-none px-0.5 text-muted hover:text-text"
          title="Ziehen zum Umordnen"
        >
          ⠿
        </span>
        <Toggle checked={effect.enabled} onChange={onToggle} ariaLabel={`${EFFECT_LABELS[effect.type]} aktiv`} />
        <span className="flex-1 truncate text-sm font-medium text-text">{EFFECT_LABELS[effect.type]}</span>
        <button
          type="button"
          disabled={index === 0}
          onClick={() => onMove(index - 1)}
          className="grid h-6 w-6 place-items-center rounded text-muted hover:text-text disabled:opacity-30"
          aria-label="nach oben"
        >
          ↑
        </button>
        <button
          type="button"
          disabled={index === count - 1}
          onClick={() => onMove(index + 1)}
          className="grid h-6 w-6 place-items-center rounded text-muted hover:text-text disabled:opacity-30"
          aria-label="nach unten"
        >
          ↓
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="grid h-6 w-6 place-items-center rounded text-muted hover:text-danger"
          aria-label="Effekt entfernen"
        >
          ×
        </button>
      </div>
      {effect.enabled && (
        <div className="border-t border-border px-2.5 py-2">
          <EffectParams effect={effect} onPatch={onPatch} />
        </div>
      )}
    </div>
  );
}

export function OpStackPanel(props: OpStackProps) {
  const isSheet = props.resultKind === "spritesheet";
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  return (
    <aside className="flex w-72 shrink-0 flex-col gap-4 overflow-y-auto border-l border-border bg-surface p-4">
      <div>
        <div className="mb-1.5 flex items-baseline justify-between gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted">Effekte</span>
          {/* Effekte gelten PRO Clip — der Kopf zeigt, welcher Clip gerade bearbeitet wird. */}
          {props.selectedClipLabel && (
            <span className="truncate rounded bg-accent/15 px-1.5 py-0.5 text-[10px] font-medium text-accent">
              {props.selectedClipLabel}
            </span>
          )}
        </div>
        <Select
          value=""
          disabled={!props.hasSelection}
          onChange={(e) => {
            const t = e.target.value as StudioEffectType | "";
            if (t) props.onAddEffect(t);
          }}
        >
          <option value="">+ Effekt hinzufügen…</option>
          {STUDIO_EFFECT_TYPES.map((t) => (
            <option key={t} value={t}>
              {EFFECT_LABELS[t]}
            </option>
          ))}
        </Select>
      </div>

      {!props.hasSelection ? (
        <p className="rounded-lg border border-dashed border-border px-3 py-4 text-center text-xs text-muted">
          Wähle unten in der Timeline einen Clip aus, um seine Effekte zu bearbeiten.
        </p>
      ) : props.effects.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border px-3 py-4 text-center text-xs text-muted">
          Noch keine Effekte auf diesem Clip. Füge z.B. „Hintergrund entfernen", „Pixelate" oder
          „Geschwindigkeit" hinzu.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {props.effects.map((effect, i) => (
            <EffectCard
              key={effect.id}
              effect={effect}
              index={i}
              count={props.effects.length}
              onToggle={(enabled) => props.onToggleEffect(effect.id, enabled)}
              onRemove={() => props.onRemoveEffect(effect.id)}
              onPatch={(patch) => props.onUpdateEffect(effect.id, patch)}
              onMove={(to) => props.onReorderEffect(i, to)}
              onDragStart={() => setDragIndex(i)}
              onDropOn={() => {
                if (dragIndex !== null && dragIndex !== i) props.onReorderEffect(dragIndex, i);
                setDragIndex(null);
              }}
            />
          ))}
        </div>
      )}

      <div>
        <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted">Ergebnis</div>
        <Segmented<StudioResultKind>
          value={props.resultKind}
          onChange={props.onResultKind}
          options={[
            { value: "spritesheet", label: "Spritesheet" },
            { value: "image", label: "Einzelframe" },
          ]}
        />
        {isSheet && (
          <div className="mt-2 flex flex-col gap-2 rounded-lg border border-border p-3">
            <Toggle checked={props.loop} onChange={props.onLoop} label="Loop (on)" />
            {props.loop && <Toggle checked={props.seam} onChange={props.onSeam} label="Naht glätten (RIFE)" />}
          </div>
        )}
      </div>

      <div className="mt-auto flex flex-col gap-2">
        <div className="text-sm text-muted">
          Preis: <span className="font-semibold text-text">{props.credits == null ? "—" : `${props.credits} cr`}</span>
        </div>
        {/* D169: Phasen-Fortschritt der laufenden Operation — Label + i/n (determinate) bzw. nur Label
            (indeterminate, ohne value). Ersetzt das stumme „Arbeitet…" durch sichtbaren Fortschritt. */}
        {props.progress && (
          <div className="space-y-1" aria-live="polite">
            <div className="flex items-center justify-between text-xs text-muted">
              <span className="truncate">{props.progress.label}</span>
              {props.progress.value !== undefined && props.progress.max !== undefined && (
                <span className="shrink-0 tabular-nums">
                  {Math.min(props.progress.value, props.progress.max)}/{props.progress.max}
                </span>
              )}
            </div>
            <ProgressBar value={props.progress.value} max={props.progress.max} label={props.progress.label} />
          </div>
        )}
        <Button variant="primary" onClick={props.onSave} disabled={props.busy || props.credits == null}>
          {props.busy ? "Arbeitet…" : isSheet ? "In Bibliothek speichern" : "Frame in Bibliothek speichern"}
        </Button>
        <Button variant="secondary" icon="download" onClick={props.onDownload} disabled={props.busy || !props.canDownload}>
          {isSheet ? "Spritesheet herunterladen" : "Frame herunterladen"}
        </Button>
        {props.canExportWebm && (
          <Button variant="secondary" onClick={props.onExportWebm} disabled={props.busy}>
            Als WebM (Alpha) exportieren
          </Button>
        )}
        {props.webmBlockedByEffects && (
          <p className="text-xs text-muted">
            WebM-Export unterstützt v1 nur Schnitt + Hintergrund entfernen — andere Effekte bitte über „herunterladen"
            sichern.
          </p>
        )}
      </div>
    </aside>
  );
}
